import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { toast } from 'vue3-toastify'
import { useAuthStore } from './authStore'
import { api } from '@/plugins/axios'

export const useCartStore = defineStore('cart', () => {
  const cart = ref([])
  const coupon = ref(null)
  const deliveryCharge = ref(0)  
  const userId = ref(null) // will be set after login
  const authStore = useAuthStore()

  // ✅ Load cart (from DB or localStorage)
  async function loadCart(uid = null) {    
    userId.value = authStore.user ? authStore.user.id : uid
    if (userId.value) {
      try {
        const res = await api.get(`/cart`)
        cart.value = res.data.results.items || []
        coupon.value = res.data.coupon || null
        deliveryCharge.value = res.data.deliveryCharge || 0
      } catch (e) {
        console.error('Failed to load cart from API:', e)
      }
    } else {
      const saved = localStorage.getItem('cart')
      if (saved) {
        const parsed = JSON.parse(saved)
        cart.value = parsed.items || []
        coupon.value = parsed.coupon || null
        deliveryCharge.value = parsed.deliveryCharge || 0
      }
    }
  }

  async function syncCart() {    
    userId.value = authStore.user ? authStore.user.id : null    
    if (userId.value && cart.value.length > 0) {
      try {
        api
          .post('/cart/sync', {
            items: cart.value       
          }).then((res) => {
            localStorage.removeItem('cart')
          })
          .catch((err) => console.error('Cart save failed:', err))
      } catch (e) {
        console.error('Failed to load cart from API:', e)
      }
    }
  }

  // ✅ Auto persist cart
  watch(
    [cart, coupon, deliveryCharge],
    () => {      
      if (userId.value && cart.value.length > 0) {
        api
          .post('/cart/sync', {
            items: cart.value       
          })
          .catch((err) => console.error('Cart save failed:', err))
      } else {
        localStorage.setItem(
          'cart',
          JSON.stringify({
            items: cart.value,
            coupon: coupon.value,
            deliveryCharge: deliveryCharge.value,
          }),
        )
      }
    },
    { deep: true },
  )

  /// ✅ Computed: Subtotal (all items)
  const subtotal = computed(() =>
    cart.value.reduce((sum, i) => sum + i.price * i.quantity, 0)
  )

  // ✅ Computed: Chocolate plaque total
  const hasChocolatePlaque = computed(() =>
    cart.value.some((item) => item.is_chocolate_plaque)
  )

  const totalChocolatePlaquePrice = computed(() =>
    cart.value.reduce((total, item) => {
      if (
        item.is_chocolate_plaque &&
        item.message_on_cake &&
        item.message_on_cake.trim() !== ''
      ) {
        total += Number(item.chocolate_plaque_price) * (item.quantity || 1)
      }
      return total
    }, 0)
  )

  // ✅ Computed: GST (10% on subtotal + chocolate plaque)
  const gst = computed(() => ((subtotal.value + totalChocolatePlaquePrice.value) * 10) / 100)

  // ✅ Computed: Discount
  const discount = computed(() => {
    let totalDiscount = 0

    // 💰 1️⃣ Coupon-based discount
    if (coupon.value) {
      if (coupon.value.type === 'percent') {
        totalDiscount += (subtotal.value * coupon.value.value) / 100
      } else if (coupon.value.type === 'flat') {
        totalDiscount += coupon.value.value
      }
    }

    // 👤 2️⃣ Extra 5% discount for first-order registered user
    const user = authStore.user
    if (user && user.is_first_online_order == 0) {
      totalDiscount += (subtotal.value * 5) / 100
    }

    return totalDiscount
  })


  // ✅ Computed: Total before delivery
  const totalBeforeDelivery = computed(() =>
    subtotal.value +
    totalChocolatePlaquePrice.value -
    discount.value
  )

  // ✅ Computed: Grand total (with delivery charge)
  const total = computed(() =>
    Math.max(0, totalBeforeDelivery.value + deliveryCharge.value)
  )

  // ✅ Computed: Cart item count
  const cartItemCount = computed(() =>
    cart.value.reduce((total, item) => total + item.quantity, 0)
  )


  // ✅ Actions
  function addToCart(product, qty = 1) {
    const existing = cart.value.find((i) => i.product_sku_id === product.product_sku_id)
    if (existing) {
      existing.quantity += qty
    } else {
      cart.value.push({ ...product, quantity: qty })      
    }
    toast.success('Product Added To Cart!')
  }

  function updateQuantity(product_sku_id, qty) {
    const item = cart.value.find((i) => i.product_sku_id === product_sku_id)
    if (item) {
      item.quantity = qty > 0 ? qty : 1
    }
  }

  function removeFromCart(product_sku_id) {
    cart.value = cart.value.filter((i) => i.product_sku_id !== product_sku_id)
    if (userId.value) {
        api
          .delete(`/cart/remove/${product_sku_id}`)
          .catch((err) => console.error('Remove item failed:', err))
      }
  }

  function applyCoupon(code) {
    const availableCoupons = {
      SKIN10: { type: 'percent', value: 10 },
      FLAT50: { type: 'flat', value: 50 },
    }
    if (availableCoupons[code]) {
      coupon.value = { code, ...availableCoupons[code] }
      return true
    }
    return false
  }

  function setDeliveryCharge(amount) {
    deliveryCharge.value = amount
  } 

  return {
    // state
    cart,
    coupon,
    deliveryCharge,
    userId,

    // getters
    subtotal,
    gst,
    discount,
    totalBeforeDelivery,
    total,
    cartItemCount,
    hasChocolatePlaque,
    totalChocolatePlaquePrice,

    // actions
    loadCart,
    syncCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    applyCoupon,
    setDeliveryCharge,
  }
})
